import type { Message, HistoryMessage, FormattedTextPart } from "../types";

// Generate unique session ID
export const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random()
    .toString(36)
    .substr(2, 9)}-${Math.random().toString(36).substr(2, 9)}-${Math.random()
    .toString(36)
    .substr(2, 4)}`;
};

// Sanitize response text
export const sanitizeResponseText = (text: string): string => {
  return text
    .replace(/\\n\\n\d+/g, "") // Remove \n\n followed by numbers
    .replace(/\\n/g, "\n") // Convert \n to actual line breaks
    .replace(/\n\n\d+$/g, "") // Remove trailing line breaks with numbers
    .trim();
};

// Create axios config
export const createAxiosConfig = (bearerToken: string) => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${bearerToken}`,
    Accept: "application/json",
  },
  timeout: 45000,
  withCredentials: false, // Disable credentials for CORS
});

// Format time
const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Check if date is yesterday
const isYesterday = (date: Date, today: Date): boolean => {
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(date, yesterday);
};

// Check if date is within this week
const isThisWeek = (date: Date, today: Date): boolean => {
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday as first day
  startOfWeek.setDate(diff);
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return date >= startOfWeek && date <= endOfWeek;
};

// Format time dynamically based on date
export const formatTime = (date: Date): string => {
  const now = new Date();

  // If it's today, show only time
  if (isSameDay(date, now)) {
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // If it's yesterday, show "Ayer HH:MM"
  if (isYesterday(date, now)) {
    const time = date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `Ayer ${time}`;
  }

  // If it's this week, show day name and time
  if (isThisWeek(date, now)) {
    const dayName = date.toLocaleDateString("es-ES", { weekday: "short" });
    const time = date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dayName} ${time}`;
  }

  // For older dates, show full date and time
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Convert history messages to chat messages
export const convertHistoryMessages = (
  historyData: HistoryMessage[],
  offset: number,
  defaultBotAvatar?: string,
  defaultUserAvatar?: string
): Message[] => {
  return historyData.map((msg: HistoryMessage, index: number) => {
    // Parse the real timestamp or fallback to fake timestamp
    let messageDate: Date;

    if (msg.date) {
      // Parse ISO 8601 date string
      messageDate = new Date(msg.date);

      // Validate the parsed date
      if (isNaN(messageDate.getTime())) {
        console.warn(`Invalid timestamp format: ${msg.date}, using fallback`);
        messageDate = new Date(Date.now() - (offset + index) * 60000);
      }
    } else {
      // Fallback to fake timestamp if no timestamp provided
      messageDate = new Date(Date.now() - (offset + index) * 60000);
    }

    return {
      id: `history-${offset}-${index}`,
      text: sanitizeResponseText(msg.message.content),
      sender: msg.message.type === "ai" ? "bot" : "user",
      timestamp: messageDate,
      avatar: msg.message.type === "ai" ? defaultBotAvatar : defaultUserAvatar,
    };
  });
};

// Parse text with Markdown-style formatting
export const parseFormattedText = (text: string): FormattedTextPart[] => {
  // First, split by lines to handle headings
  const lines = text.split("\n");
  const allParts: FormattedTextPart[] = [];

  lines.forEach((line, lineIndex) => {
    // Add line break between lines (except for first line)
    if (lineIndex > 0) {
      allParts.push({ text: "\n", type: "normal" });
    }

    // Check for headings first (must be at start of line)
    if (line.match(/^### /)) {
      const headingText = line.replace(/^### /, "");
      allParts.push({ text: headingText, type: "heading3" });
      return;
    }

    if (line.match(/^## /)) {
      const headingText = line.replace(/^## /, "");
      allParts.push({ text: headingText, type: "heading2" });
      return;
    }

    if (line.match(/^# /)) {
      const headingText = line.replace(/^# /, "");
      allParts.push({ text: headingText, type: "heading1" });
      return;
    }

    // Process inline formatting for non-heading lines
    const lineParts = parseInlineFormatting(line);
    allParts.push(...lineParts);
  });

  return allParts;
};

// Parse inline formatting (bold, italic, etc.) within a line
const parseInlineFormatting = (text: string): FormattedTextPart[] => {
  if (!text.trim()) {
    return [{ text, type: "normal" }];
  }

  const parts: FormattedTextPart[] = [];
  let remainingText = text;
  let processed = false;

  // Process text iteratively to handle multiple formatting
  while (remainingText.length > 0 && !processed) {
    let foundMatch = false;

    // Try to find the first formatting match
    const patterns = [
      { regex: /```([^`]+?)```/, type: "code" as const, markers: 6 }, // \`\`\`code\`\`\` (3 chars each side)
      { regex: /\*\*([^*]+?)\*\*/, type: "bold" as const, markers: 4 }, // **bold** (2 chars each side)
      { regex: /\*([^*]+?)\*/, type: "italic" as const, markers: 2 }, // *italic* (1 char each side)
      { regex: /~~([^~]+?)~~/, type: "strikethrough" as const, markers: 4 }, // ~~strike~~ (2 chars each side)
      { regex: /`([^`]+?)`/, type: "monospace" as const, markers: 2 }, // `mono` (1 char each side)
    ];

    let earliestMatch: {
      match: RegExpMatchArray;
      type: FormattedTextPart["type"];
      markers: number;
      index: number;
    } | null = null;

    // Find the earliest match in the text
    patterns.forEach(({ regex, type, markers }) => {
      const match = remainingText.match(regex);
      if (match && match.index !== undefined) {
        if (!earliestMatch || match.index < earliestMatch.index) {
          earliestMatch = { match, type, markers, index: match.index };
        }
      }
    });

    if (earliestMatch) {
      const { match, type, index } = earliestMatch;

      // Add text before the match as normal text
      if (index > 0) {
        const beforeText = remainingText.substring(0, index);
        parts.push({ text: beforeText, type: "normal" });
      }

      // Add the formatted text
      parts.push({ text: match[1], type });

      // Continue with the remaining text after this match
      remainingText = remainingText.substring(index + match[0].length);
      foundMatch = true;
    }

    if (!foundMatch) {
      // No more matches found, add remaining text as normal
      if (remainingText) {
        parts.push({ text: remainingText, type: "normal" });
      }
      processed = true;
    }
  }

  // If no parts were created, return the original text as normal
  if (parts.length === 0) {
    parts.push({ text, type: "normal" });
  }

  return parts;
};

// Preview formatted text for input display
export const getFormattedPreview = (text: string): string => {
  const parts = parseFormattedText(text);
  return parts
    .map((part) => {
      switch (part.type) {
        case "bold":
          return `**${part.text}**`;
        case "italic":
          return `*${part.text}*`;
        case "strikethrough":
          return `~~${part.text}~~`;
        case "code":
          return `\`\`\`${part.text}\`\`\``;
        case "monospace":
          return `\`${part.text}\``;
        case "heading1":
          return `# ${part.text}`;
        case "heading2":
          return `## ${part.text}`;
        case "heading3":
          return `### ${part.text}`;
        default:
          return part.text;
      }
    })
    .join("");
};

// Get plain text without formatting
export const getPlainText = (text: string): string => {
  return text
    .replace(/^### /gm, "") // Remove ### headings
    .replace(/^## /gm, "") // Remove ## headings
    .replace(/^# /gm, "") // Remove # headings
    .replace(/\*\*([^*]+?)\*\*/g, "$1") // Remove **bold**
    .replace(/\*([^*]+?)\*/g, "$1") // Remove *italic*
    .replace(/~~([^~]+?)~~/g, "$1") // Remove ~~strikethrough~~
    .replace(/```([^`]+?)```/g, "$1") // Remove \`\`\`code\`\`\`
    .replace(/`([^`]+?)`/g, "$1"); // Remove `monospace`
};

// Check if text has any formatting
export const hasFormatting = (text: string): boolean => {
  const patterns = [
    /^### /, // ### heading
    /^## /, // ## heading
    /^# /, // # heading
    /\*\*([^*]+?)\*\*/, // **bold**
    /\*([^*]+?)\*/, // *italic*
    /~~([^~]+?)~~/, // ~~strikethrough~~
    /```([^`]+?)```/, // \`\`\`code\`\`\`
    /`([^`]+?)`/, // `monospace`
  ];

  return patterns.some((pattern) => pattern.test(text));
};

// Format examples for help (Enhanced Markdown syntax)
export const FORMATTING_EXAMPLES = [
  { format: "**texto**", description: "Negrita", example: "**Hola mundo**" },
  { format: "*texto*", description: "Cursiva", example: "*Texto en cursiva*" },
  /*{ format: "~~texto~~", description: "Tachado", example: "~~Texto tachado~~" },
  { format: "### texto", description: "Encabezado", example: "### Mi título" },
  { format: "`texto`", description: "Código", example: "`código inline`" },
  { format: "```texto```", description: "Bloque código", example: '```console.log("Hola")```' },*/
];
